import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Image } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useNetwork } from '../../contexts/NetworkContext'
import { CardContent, CardIcon } from '@/components/ui/card'
import { AuthService } from '@/services/core/auth'
import { CreativesApi, Creative } from '@/services/creatives/creativesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { CreativeTable } from '../../components/CreativeTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import {
  CreativeFilters,
  DEFAULT_CREATIVE_FILTERS,
  creativeFetchFilters,
  type CreativeFilterState,
} from '../../components/CreativeFilters'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function CreativesPage() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady } = useNetwork()
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()
  const [stored, setFilters] = usePersistedState<CreativeFilterState>('filters.creatives', DEFAULT_CREATIVE_FILTERS)
  // A remembered value predates any filter added since, so the defaults fill in the rest.
  const filters: CreativeFilterState = { ...DEFAULT_CREATIVE_FILTERS, ...stored }
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<Creative[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    CreativesApi.getPage({
      page,
      pageSize,
      search,
      status: filters.objectState,
      sort,
      ...creativeFetchFilters(filters),
    })

  const { data, isLoading } = useQuery({
    queryKey: ['creatives', 'page', scopeKey, search, filters, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken && scopeReady,
  })
  const creatives = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((c) => CreativesApi.update(c.id, { name: c.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['creatives'] })
      toast.success(t('common.done'), { duration: 3000 })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('creatives.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('creatives.title')}</h1>
            <p>{t('creatives.subtitle')}</p>
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)} tone="creative">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Image} />
                {t('creatives.creativeCount', { count: totalCount })}
              </>
            }
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <CreativeFilters
                  filters={filters}
                  onChange={(v) => refilter(() => setFilters(v))}
                />
              </>
            }
          />

          {selection.length > 0 && (
            <BulkActionBar
              count={selection.length}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
              onClear={() => setSelection([])}
            />
          )}

          <CardContent className="p-0">
            <CreativeTable
              creatives={creatives}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('creatives.creativeCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
